// src/pages/CheckoutSuccess.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCart } from "../context/CartContext";

/* -------- Helpers -------- */
const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : null;
};
const fmtMoney = (v, cc = "XOF") => (v == null ? "—" : `${Math.round(v)} ${cc}`);

const STATUS_VIEW = {
  paid:    { label: "Paiement réussi",       cls: "text-success",  badge: "bg-success",  icon: "bi-check-circle-fill" },
  pending: { label: "Paiement en attente",   cls: "text-warning",  badge: "bg-warning text-dark", icon: "bi-hourglass-split" },
  failed:  { label: "Paiement échoué / annulé", cls: "text-danger",  badge: "bg-danger",  icon: "bi-x-circle-fill" },
  unknown: { label: "Statut inconnu",         cls: "text-secondary", badge: "bg-secondary", icon: "bi-question-circle-fill" },
};

const mapProviderToUnified = (s) => {
  const v = String(s || "").toLowerCase();
  if (["success", "approved", "completed", "paid"].includes(v)) return "paid";
  if (["failed", "error", "canceled", "cancelled", "refused"].includes(v)) return "failed";
  if (["pending", "processing"].includes(v)) return "pending";
  return "unknown";
};

function getApiBase() {
  const env = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
  if (env) return env;
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    return "http://localhost:5000";
  }
  return ""; // même domaine (Vercel)
}
const API_BASE = getApiBase();

/* -------- Récup produits de commande -------- */
function extractOrderItems(verifyResult) {
  const fromApi =
    verifyResult?.order?.items ||
    verifyResult?.items ||
    verifyResult?.meta?.items;
  if (Array.isArray(fromApi) && fromApi.length) return sanitizeItems(fromApi);

  try {
    const raw = localStorage.getItem("last_order_snapshot_v1");
    if (!raw) return [];
    const snap = JSON.parse(raw);
    const items = snap?.items || snap?.cart?.items || [];
    if (Array.isArray(items) && items.length) return sanitizeItems(items);
  } catch {
    /* ignore */
  }
  return [];
}
function sanitizeItems(items) {
  return items.map((it, idx) => ({
    id: it.productId || it.id || `i${idx}`,
    title: it.title || it.name || "Article",
    image: it.image || it.images?.[0] || "",
    qty: toInt(it.qty) ?? 1,
    price: toInt(it.price) ?? 0,
    variantLabel: it.variantLabel || it.variant?.label || it.variant,
    slug: it.slug,
  }));
}

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const { clear } = useCart();

  const identifier = params.get("identifier") || params.get("orderId") || "";
  const offline = params.get("offline") === "1";
  const statusQP = (params.get("status") || "").toLowerCase();
  const currencyQP = params.get("currency") || "XOF";

  // État vérification
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const unifiedFromQuery = mapProviderToUnified(statusQP);

  // --------- Vidage panier (1 seule fois par identifiant) ----------
  const clearOnce = useCallback(() => {
    if (!identifier) return;
    const key = "cart_cleared_for";
    if (sessionStorage.getItem(key) === identifier) return;
    clear();
    sessionStorage.setItem(key, identifier);
    localStorage.removeItem("last_order_snapshot_v1");
  }, [identifier, clear]);

  // 1) OFFLINE => vider direct
  useEffect(() => {
    if (offline) clearOnce();
  }, [offline, clearOnce]);

  // 2) Query param déjà "success" => vider
  useEffect(() => {
    if (!offline && unifiedFromQuery === "paid") clearOnce();
  }, [offline, unifiedFromQuery, clearOnce]);

  // 3) Si la vérif confirme => vider
  useEffect(() => {
    const unified = mapProviderToUnified(verifyResult?.provider_status || verifyResult?.status);
    if (unified === "paid") clearOnce();
  }, [verifyResult, clearOnce]);

  // ✅ --------- Vérification serveur (useCallback) ----------
  const verifyServer = useCallback(async () => {
    if (!identifier) return;
    setVerifying(true);
    setVerifyError("");
    try {
      const url = `${API_BASE}/api/payments/verify?identifier=${encodeURIComponent(identifier)}`;
      const r = await fetch(url, { method: "GET" });
      const txt = await r.text();
      let data;
      try {
        data = JSON.parse(txt);
      } catch {
        throw new Error(`Réponse non-JSON (${r.status})`);
      }
      if (!r.ok) {
        throw new Error(data?.message || data?.error || `HTTP ${r.status}`);
      }
      setVerifyResult(data);
    } catch (e) {
      setVerifyError(e?.message || "Échec de la vérification.");
    } finally {
      setVerifying(false);
    }
  }, [identifier]);

  // ✅ Auto-verify 1 fois si en ligne et pas offline
  useEffect(() => {
    let done = false;
    if (!offline && identifier) {
      (async () => {
        try {
          if (done) return;
          await verifyServer();
        } catch {
          /* noop */
        }
      })();
    }
    return () => {
      done = true;
    };
  }, [identifier, offline, verifyServer]);

  // --------- Dérivés d’affichage ----------
  const currency = verifyResult?.currency || currencyQP || "XOF";

  const amountPaid = useMemo(() => {
    if (verifyResult?.amount != null) return toInt(verifyResult.amount);
    const qp = toInt(params.get("amount"));
    return qp;
  }, [verifyResult, params]);

  const fee = useMemo(() => {
    if (verifyResult?.meta?.fee_xof != null) return toInt(verifyResult.meta.fee_xof);
    const qp = toInt(params.get("fee_xof"));
    return qp;
  }, [verifyResult, params]);

  const subtotal = useMemo(() => {
    if (verifyResult?.meta?.subtotal_xof != null) return toInt(verifyResult.meta.subtotal_xof);
    const qp = toInt(params.get("subtotal_xof"));
    if (qp != null) return qp;
    if (amountPaid != null && fee != null) return amountPaid - fee;
    return null;
  }, [verifyResult, amountPaid, fee, params]);

  const providerStatus = verifyResult?.provider_status || statusQP || "pending";
  const unifiedStatus = mapProviderToUnified(providerStatus);
  const statusView = STATUS_VIEW[unifiedStatus] || STATUS_VIEW.unknown;

  const provider = verifyResult?.provider || params.get("provider") || "PayGate";
  const phone = verifyResult?.phone || params.get("phone") || "—";
  const network = verifyResult?.network || params.get("network") || params.get("channel") || "—";
  const reference = verifyResult?.reference || params.get("reference") || params.get("payment_reference") || "—";

  // Produits de la commande
  const items = useMemo(() => extractOrderItems(verifyResult), [verifyResult]);
  const itemsSubtotal = useMemo(
    () => items.reduce((s, it) => s + (Number(it.price || 0) * Number(it.qty || 1)), 0),
    [items]
  );

  const onCopyId = useCallback(async () => {
    if (!identifier) return;
    try {
      await navigator.clipboard.writeText(identifier);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  }, [identifier]);

  return (
    <div className="container py-4 py-md-5" style={{ maxWidth: 1080 }}>
      {/* ======= BANNER ======= */}
      <section className="mb-4">
        <div
          className="rounded-4 overflow-hidden shadow-sm position-relative"
          style={{
            minHeight: 160,
            background:
              unifiedStatus === "paid"
                ? "linear-gradient(135deg, #34d399 0%, #10b981 50%, #059669 100%)"
                : unifiedStatus === "failed"
                ? "linear-gradient(135deg, #f87171 0%, #ef4444 50%, #dc2626 100%)"
                : "linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage:
                unifiedStatus === "paid"
                  ? "url('https://images.unsplash.com/photo-1517249361621-f11084eb8aa4?q=80&w=1600')"
                  : unifiedStatus === "failed"
                  ? "url('https://images.unsplash.com/photo-1501159599894-155982264a55?q=80&w=1600')"
                  : "url('https://images.unsplash.com/photo-1517504734587-2890819debab?q=80&w=1600')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              mixBlendMode: "overlay",
              opacity: 0.15,
            }}
          />
          <div className="p-3 p-md-5 position-relative text-white">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white text-success"
                style={{ width: 64, height: 64, border: "2px solid rgba(255,255,255,.35)" }}
                aria-hidden="true"
              >
                <i className={`bi ${statusView.icon}`} style={{ fontSize: 32 }} />
              </div>
              <div>
                <h2 className="h4 mb-1">{statusView.label}</h2>
                <div className="small">
                  Identifiant : <strong className="text-white">{identifier || "—"}</strong>
                  {providerStatus && (
                    <span className={`badge ${statusView.badge} ms-2`}>{providerStatus}</span>
                  )}
                </div>
              </div>
              <div className="ms-auto d-none d-md-block">
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  onClick={onCopyId}
                  disabled={!identifier}
                >
                  {copied ? "Copié !" : "Copier l’identifiant"}
                </button>
              </div>
            </div>

            {unifiedStatus === "paid" ? (
              <p className="mb-0 mt-3 small opacity-90">
                Merci pour votre achat 🙌 Nous préparons votre commande. Un e-mail/SMS de confirmation vous sera envoyé.
              </p>
            ) : unifiedStatus === "pending" ? (
              <p className="mb-0 mt-3 small opacity-90">
                Votre paiement est en cours de traitement. Vous pourrez vérifier à nouveau l’état dans un instant.
              </p>
            ) : unifiedStatus === "failed" ? (
              <p className="mb-0 mt-3 small opacity-90">
                Le paiement n’a pas abouti. Réessayez ou choisissez une autre méthode. Si un débit apparaît, contactez le support avec la référence.
              </p>
            ) : (
              <p className="mb-0 mt-3 small opacity-90">
                Statut non déterminé. Utilisez le bouton ci-dessous pour re-vérifier auprès du serveur.
              </p>
            )}

            <div className="d-flex gap-2 mt-3">
              <button
                type="button"
                className="btn btn-outline-light btn-sm"
                onClick={verifyServer}
                disabled={verifying || !identifier}
                title="Confirmer le statut auprès de votre backend (recommandé)"
              >
                {verifying ? "Vérification…" : "Vérifier le statut"}
              </button>
              <Link to="/" className="btn btn-light btn-sm">
                Continuer mes achats
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ======= GRID ======= */}
      <div className="row g-3 g-md-4">
        {/* Détails transaction */}
        <div className="col-12 col-lg-5 order-2 order-lg-1">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <h5 className="card-title m-0">Détails de la transaction</h5>
                {identifier && (
                  <button className="btn btn-sm btn-outline-secondary" onClick={onCopyId}>
                    {copied ? "Copié !" : "Copier l’identifiant"}
                  </button>
                )}
              </div>

              <div className="row g-3">
                <div className="col-12">
                  <div className="small text-muted">Identifiant</div>
                  <div className="fw-semibold text-break">{identifier || "—"}</div>
                </div>
                <div className="col-6 col-md-6">
                  <div className="small text-muted">Réseau</div>
                  <div className="fw-semibold">{network}</div>
                </div>
                <div className="col-6 col-md-6">
                  <div className="small text-muted">Téléphone</div>
                  <div className="fw-semibold">{phone}</div>
                </div>
                <div className="col-6 col-md-6">
                  <div className="small text-muted">Fournisseur</div>
                  <div className="fw-semibold">{provider}</div>
                </div>
                <div className="col-6 col-md-6">
                  <div className="small text-muted">Référence</div>
                  <div className="fw-semibold text-break">{reference}</div>
                </div>
              </div>

              <hr className="my-4" />

              <h6 className="mb-3">Montants</h6>
              <div className="d-flex justify-content-between">
                <span>Sous-total articles</span>
                <strong>{fmtMoney(subtotal ?? itemsSubtotal, currency)}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Frais de paiement</span>
                <strong>{fmtMoney(fee, currency)}</strong>
              </div>
              <div className="d-flex justify-content-between border-top pt-2">
                <span>Total payé</span>
                <strong className="fs-5">{fmtMoney(amountPaid, currency)}</strong>
              </div>

              {verifyError && <div className="alert alert-danger mt-3 mb-0">{verifyError}</div>}
            </div>
          </div>
        </div>

        {/* Articles de la commande */}
        <div className="col-12 col-lg-7 order-1 order-lg-2">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Articles de la commande</h5>

              {items.length === 0 ? (
                <div className="alert alert-light mb-0">
                  Impossible d’afficher les articles.
                  {unifiedStatus === "paid" ? " Votre commande est bien enregistrée." : ""}
                  {identifier ? " Vous pouvez consulter l’historique depuis « Suivre ma commande »." : ""}
                </div>
              ) : (
                <>
                  <ul className="list-group list-group-flush">
                    {items.map((it) => (
                      <li key={it.id} className="list-group-item py-3">
                        <div className="d-flex align-items-center gap-3">
                          {it.image ? (
                            <img
                              src={it.image}
                              alt={it.title}
                              style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8 }}
                            />
                          ) : (
                            <div className="bg-light rounded" style={{ width: 64, height: 64 }} />
                          )}
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between">
                              <div>
                                <div className="fw-semibold">{it.title}</div>
                                {it.variantLabel && <div className="text-muted small">{it.variantLabel}</div>}
                                <div className="text-muted small">Qté : {it.qty}</div>
                              </div>
                              <div className="text-end">
                                <div className="fw-semibold">{fmtMoney(it.price, currency)}</div>
                                <div className="small text-muted">{fmtMoney(it.price * it.qty, currency)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <div className="d-flex justify-content-between mt-3">
                    <span className="text-muted">Sous-total articles</span>
                    <strong>{fmtMoney(itemsSubtotal, currency)}</strong>
                  </div>
                </>
              )}

              <div className="d-grid gap-2 mt-4">
                <Link to="/orders/lookup-phone" className="btn btn-outline-success">
                  Suivre ma commande
                </Link>
                <Link to="/catalogue" className="btn btn-primary">
                  Continuer mes achats
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 575.98px){
          .card-body pre { font-size: 12px; }
        }
      `}</style>
    </div>
  );
}
